import { useState, useEffect } from "react"
import { Box, Flex, Text } from "@chakra-ui/react"
import axios from "axios"
import { Line } from "react-chartjs-2"
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
} from "chart.js"
import BentoCard from "../BentoCard"
import DetailsModal from "../DetailsModal"
import DailySales from "../../DailySales"
import { neumorph } from "../neumorph"
import { usePeriod } from "../PeriodContext"
import { formatCurrency, formatNum } from "../KpiHeader"

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip)

const SalesVolumeCard = ({ supplier, retailer }) => {
  const { startDate, endDate } = usePeriod()
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState([])
  const [modalOpen, setModalOpen] = useState(false)

  useEffect(() => {
    if (!supplier || !retailer) return
    const controller = new AbortController()
    const fetchData = async () => {
      setLoading(true)
      try {
        const res = await axios.post(
          `${process.env.REACT_APP_API_URL}/insights/daily-sales`,
          { supplier, retailer, userid: localStorage.getItem("userid"), startDate, endDate },
          { signal: controller.signal }
        )
        if (res.data?.status === 1) setData(res.data.data || [])
        else setData([])
      } catch (err) {
        if (!axios.isCancel(err)) console.error("SalesVolumeCard fetch error:", err)
      } finally {
        setLoading(false)
      }
    }
    fetchData()
    return () => controller.abort()
  }, [supplier, retailer, startDate, endDate])

  const totalUnits = data.reduce((s, r) => s + (parseInt(r.total_units) || 0), 0)
  const totalAmount = data.reduce((s, r) => s + (parseFloat(r.total_amount) || 0), 0)

  // Detect granularity from gap between first 2 dates
  let granularity = "daily"
  if (data.length >= 2) {
    const d1 = new Date(data[0].day)
    const d2 = new Date(data[1].day)
    const diff = Math.round((d2 - d1) / (1000 * 60 * 60 * 24))
    if (diff >= 28) granularity = "monthly"
    else if (diff >= 6) granularity = "weekly"
  }

  const chartData = data.length
    ? {
        labels: data.map((r) => r.day),
        datasets: [
          {
            label: "Revenue",
            data: data.map((r) => parseFloat(r.total_amount) || 0),
            borderColor: neumorph.accentTealDark,
            backgroundColor: "rgba(4, 174, 193, 0.15)",
            borderWidth: 2,
            pointRadius: 0,
            tension: 0.3,
            fill: true,
          },
        ],
      }
    : null

  const fmtCompactRand = (v) => {
    if (v >= 1_000_000) return `R${(v / 1_000_000).toFixed(1)}M`
    if (v >= 1_000) return `R${(v / 1_000).toFixed(0)}k`
    return `R${v}`
  }

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { mode: "index", intersect: false },
      datalabels: { display: false },
    },
    scales: {
      x: { display: false },
      y: {
        display: true,
        beginAtZero: true,
        grid: { color: "rgba(174,184,200,0.15)", drawBorder: false },
        ticks: { font: { size: 9 }, color: neumorph.textMuted, maxTicksLimit: 4, callback: fmtCompactRand },
      },
    },
  }

  return (
    <>
      <BentoCard
        title={
          granularity === "daily"
            ? "Daily Sales Volume"
            : granularity === "weekly"
            ? "Weekly Sales Volume"
            : "Monthly Sales Volume"
        }
        subtitle={`${data.length} data point${data.length !== 1 ? "s" : ""}`}
        loading={loading}
        onDetails={() => setModalOpen(true)}
      >
        <Flex direction="column" h="100%" justify="space-between">
          <Flex gap={3} mb={2}>
            <Box>
              <Text fontSize="2xs" color={neumorph.textMuted}>
                Total Revenue
              </Text>
              <Text fontSize="md" fontWeight="700" color={neumorph.accentTealDark}>
                {formatCurrency(totalAmount)}
              </Text>
            </Box>
            <Box>
              <Text fontSize="2xs" color={neumorph.textMuted}>
                Units
              </Text>
              <Text fontSize="md" fontWeight="700" color={neumorph.textPrimary}>
                {formatNum(totalUnits)}
              </Text>
            </Box>
          </Flex>
          {chartData ? (
            <Box h="110px">
              <Line data={chartData} options={chartOptions} />
            </Box>
          ) : (
            <Text fontSize="xs" color={neumorph.textMuted}>
              No data
            </Text>
          )}
        </Flex>
      </BentoCard>
      <DetailsModal isOpen={modalOpen} onClose={() => setModalOpen(false)} title="Sales Volume & Day-of-Week">
        <DailySales supplier={supplier} retailer={retailer} />
      </DetailsModal>
    </>
  )
}

export default SalesVolumeCard
